import React, { useEffect, useRef, useState } from 'react';
import BookmarkIcon from 'assets/Bookmark.svg';
import Copy from 'assets/copy.svg';
import SquareList from 'assets/SquereList.svg';
import { Button, message as messageAntd, Tag } from 'antd';
import {
  CloseOutlined,
  DownCircleTwoTone,
  ExpandAltOutlined,
  FileTextFilled,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Messages, useChatApi } from 'api/useChat';
import { useChatState } from '../state/chatState';
import FeedbackPopconfirm from './FeedbackPopconfirm';
import './ChatMessage.scss';
import Paragraph from 'antd/lib/typography/Paragraph';
// Define the component props
interface ChatMessageProps {
  message: Messages;
  isLatestAssistantMessage?: boolean;
  isStreaming?: boolean;
  tabType?: 'conversations' | 'bookmarks' | 'quickCheckReports';
  files?: string[];
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLatestAssistantMessage = false,
  isStreaming = false,
  tabType = 'conversations',
  files
}) => {
  const { type, content } = message;
  const { bookmarkMessage, state } = useChatState();
  const { submitMessageFeedback } = useChatApi();
  const [isBookmarking, setIsBookmarking] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null);
  const [isPopconfirmVisible, setIsPopconfirmVisible] = useState(false);
  const [savedFeedbackRating, setSavedFeedbackRating] = useState<string | null>(
    (message as Messages & { responseMetadata?: { feedbackRating?: string } }).responseMetadata?.feedbackRating || null
  );
  const renderedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsBookmarked(message.metadata && 'isBookmarked' in message.metadata ? message.metadata.isBookmarked : false);
  }, [state.bookmarks]);

  const handleBookmark = async () => {
    if ('id' in message) {
      setIsBookmarking(true);
      try {
        await bookmarkMessage(message.id, {
          metadata: {
            isBookmarked: !isBookmarked
          }
        });
        setIsBookmarked(!isBookmarked);
      } catch (error) {
        console.error('Error bookmarking message:', error);
      } finally {
        setIsBookmarking(false);
      }
    }
  };

  const handleRemoveBookmark = async () => {
    await bookmarkMessage(message.id, {
      metadata: {
        isBookmarked: false
      }
    });
  };
  const handleSubmitMessageFeedback = (status: string) => {
    setFeedbackStatus(status);
    setIsPopconfirmVisible(true);
  };

  const handleConfirmFeedback = async (textFeedback: string) => {
    if (feedbackStatus) {
      await submitMessageFeedback(state.currentSessionId?.id || '', message.id, feedbackStatus, textFeedback);
      setSavedFeedbackRating(feedbackStatus);
      messageAntd.success('Thank you for your feedback');
      setFeedbackStatus(null);
      setIsPopconfirmVisible(false);
    }
  };

  const handleCancelFeedback = async () => {
    if (feedbackStatus) {
      // Submit feedback without text if user clicks Skip
      await submitMessageFeedback(state.currentSessionId?.id || '', message.id, feedbackStatus, '');
      setSavedFeedbackRating(feedbackStatus);
      messageAntd.success('Thank you for your feedback');
    }
    setFeedbackStatus(null);
    setIsPopconfirmVisible(false);
  };

  const handlePopconfirmVisibleChange = (visible: boolean) => {
    setIsPopconfirmVisible(visible);
    if (!visible && feedbackStatus) {
      // If popconfirm is closed without clicking confirm/cancel (e.g., clicking outside),
      // reset the state without submitting
      setFeedbackStatus(null);
    }
  };
  const handleCopy = () => {
    if (renderedRef.current) {
      const textToCopy = renderedRef.current.innerHTML;
      const blob = new Blob([textToCopy], { type: 'text/html' });
      const data = [new ClipboardItem({ 'text/html': blob })];
      navigator.clipboard
        .write(data)
        .then(() => messageAntd.success('Copied to clipboard'))
        .catch((err) => {
          messageAntd.error('Could not copy. Please try again.');
          console.error('Copy failed:', err);
        });
    }
  };

  const markdownContent = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
      }}
    >
      {content}
    </ReactMarkdown>
  );

  if (type === 'human') {
    return (
      <div className="p-3 mb-3 rounded-lg bg-blue-50 ml-auto max-w-[90%] w-fit mr-2">
        <div>
          <div ref={renderedRef}>{content}</div>
          {files && (
            <div className="flex flex-wrap mt-2">
              {files?.map((file) => (
                <Tag key={file} className="mr-2 mb-1 p-1 font-bold  leading-5 rounded-lg">
                  {file}
                </Tag>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            type="link"
            onClick={() => handleCopy()}
            icon={<img className="opacity-50 hover:opacity-100" src={Copy} alt="Copy" width={16} height={16} />}
          />
        </div>
      </div>
    );
  }

  if (type === 'assistantResponse' || type === 'ai') {
    // if (tabType === 'bookmarks' || tabType === 'quickCheckReports') {
    //   return (
    //     <div className="p-3 mb-3 rounded-lg bg-gray-50">
    //       <div ref={renderedRef} className={`markdown-content ${isExpanded ? '' : 'max-h-[100px] overflow-hidden'} `}>
    //         {markdownContent}
    //       </div>

    //       <div className="flex justify-between items-center mt-2">
    //         <div className="text-sm text-gray-500 flex items-center">
    //           {tabType === 'quickCheckReports' ? (
    //             <FileTextFilled className="w-[14px] h-[14px] mr-1" />
    //           ) : (
    //             <Button
    //               type="link"
    //               icon={<img className="opacity-50" src={SquareList} alt="Bookmark" width={16} height={16} />}
    //             />
    //           )}
    //           {message?.createdAt
    //             ? new Date(message.createdAt).toLocaleString('en-US', {
    //                 month: 'long',
    //                 day: 'numeric',
    //                 year: 'numeric',
    //                 hour: '2-digit',
    //                 minute: '2-digit',
    //                 hour12: true
    //               })
    //             : ''}
    //         </div>
    //         <div className="flex items-center">
    //           <Button
    //             type="link"
    //             onClick={() => handleCopy()}
    //             icon={<img className="opacity-50 hover:opacity-100" src={Copy} alt="Copy" width={16} height={16} />}
    //           />
    //           <Button
    //             type="link"
    //             onClick={() => setIsExpanded(!isExpanded)}
    //             icon={<DownCircleTwoTone className={`${isExpanded ? 'rotate-180' : ''}`} />}
    //           >
    //             {isExpanded ? 'Collapse' : 'Expand'}
    //           </Button>
    //           {tabType === 'bookmarks' && (
    //             <Button type="link" danger onClick={handleRemoveBookmark} icon={<CloseOutlined />}>
    //               Remove Bookmark
    //             </Button>
    //           )}
    //         </div>
    //       </div>
    //     </div>
    //   );
    // }

    return (
      <div className="p-3 pb-1 mb-3 rounded-lg bg-gray-50  mr-auto max-w-[90%] ">
        <div className="flex flex-col">
          <div ref={renderedRef} className="markdown-content">
            {markdownContent}
          </div>
          {(isLatestAssistantMessage ? !isStreaming : true) && (
            <div className="flex justify-end">
              <span className="text-sm text-gray-500 mt-2">
                {message?.createdAt
                  ? new Date(message.createdAt).toLocaleString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })
                  : ''}
              </span>
              {/* <Button
                type="link"
                icon={
                  isBookmarked ? (
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={SquareList}
                      alt="Bookmarked"
                      width={16}
                      height={16}
                    />
                  ) : (
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={BookmarkIcon}
                      alt="Bookmark"
                      width={16}
                      height={16}
                    />
                  )
                }
                loading={isBookmarking}
                onClick={handleBookmark}
                disabled={isBookmarking || isStreaming}
              /> */}
              <FeedbackPopconfirm
                visible={isPopconfirmVisible && feedbackStatus === 'positive'}
                onVisibleChange={handlePopconfirmVisibleChange}
                onConfirm={handleConfirmFeedback}
                onCancel={handleCancelFeedback}
              >
                <Button
                  type="link"
                  icon={
                    savedFeedbackRating === 'positive' ? (
                      <LikeFilled className="text-blue-500" />
                    ) : (
                      <LikeOutlined className="opacity-50 hover:opacity-100" />
                    )
                  }
                  disabled={isStreaming}
                  onClick={() => handleSubmitMessageFeedback('positive')}
                />
              </FeedbackPopconfirm>
              <FeedbackPopconfirm
                visible={isPopconfirmVisible && feedbackStatus === 'negative'}
                onVisibleChange={handlePopconfirmVisibleChange}
                onConfirm={handleConfirmFeedback}
                onCancel={handleCancelFeedback}
              >
                <Button
                  type="link"
                  icon={
                    savedFeedbackRating === 'negative' ? (
                      <DislikeFilled className="text-red-500" />
                    ) : (
                      <DislikeOutlined className="opacity-50 hover:opacity-100" />
                    )
                  }
                  disabled={isStreaming}
                  onClick={() => handleSubmitMessageFeedback('negative')}
                />
              </FeedbackPopconfirm>

              <Button
                type="link"
                onClick={() => handleCopy()}
                icon={<img className="opacity-50 hover:opacity-100" src={Copy} alt="Copy" width={16} height={16} />}
                disabled={isStreaming}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === 'toolUse' || type === 'aiThinking') {
    const intermediateMessage = (
      <div
        className={` shimmer intermediate-message ${
          type === 'toolUse' ? 'tool-use' : type === 'aiThinking' ? 'ai-thinking' : ''
        }  `}
      >
        {markdownContent}
      </div>
    );

    return intermediateMessage;
  }

  return null;
};

export default ChatMessage;
